/**
 * Frontend types + helpers for the Phase 2 permission-aware retrieval API.
 *
 * These interfaces MIRROR the backend response models in
 * `apps/api/src/routers/retrieval.py` (task-16). The backend is the authoritative
 * owner of the wire shape; this file exists so the Retrieval + Knowledge pages and
 * their tests share one typed contract instead of re-declaring it inline.
 *
 * Endpoints
 *   - POST /api/retrieval/query   -> RetrievalQueryResponse
 *   - GET  /api/retrieval/documents -> DocumentsResponse
 *
 * The "entitlement" story: the backend never leaks the title/content of a
 * suppressed document — only counts. The UI surfaces those counts (returned vs.
 * candidates, plus a leak-free withheld banner) so switching personas visibly
 * changes results for the SAME query.
 */

import type { SensitivityTier } from './constants';

/** A single permitted document/chunk the current persona may see. */
export interface PermittedDoc {
  doc_id: string | null;
  chunk_id: string | null;
  title: string | null;
  source_system: string | null;
  source_url: string | null;
  site_or_drive: string | null;
  /** Sensitivity tier strings (e.g. 'ITAR', 'CUI'); may be unknown values. */
  sensitivity: string[];
  last_modified: string | null;
}

/** A citation backing the grounded answer. Only permitted docs are ever cited. */
export interface Citation {
  marker: string;
  doc_id: string | null;
  chunk_id: string;
  title: string | null;
  source_system: string | null;
  source_url: string | null;
  site_or_drive: string | null;
  sensitivity: string[];
  snippet: string;
}

/**
 * Suppression accounting — counts only, never identifying content.
 * Invariant: candidates === returned + total, and total === for_acl + for_group.
 */
export interface ExcludedCounts {
  total: number;
  for_acl: number;
  for_group: number;
}

/** Response of POST /api/retrieval/query. */
export interface RetrievalQueryResponse {
  answer: string;
  citations: Citation[];
  permitted_docs: PermittedDoc[];
  /** Permission decision label, e.g. 'allow', 'partial', 'deny'. */
  decision: string;
  candidates_count: number;
  returned_count: number;
  excluded_counts: ExcludedCounts;
  /** Leak-free human-readable banner; null when nothing was withheld. */
  suppression_notice: string | null;
}

/** Body of POST /api/retrieval/query. */
export interface RetrievalQueryRequest {
  query: string;
  top_k?: number;
}

/** Response of GET /api/retrieval/documents. */
export interface DocumentsResponse {
  documents: PermittedDoc[];
  total_in_corpus: number;
  permitted_count: number;
  withheld_count: number;
  suppression_notice: string | null;
}

/**
 * Narrow an arbitrary sensitivity string from the API to a known
 * {@link SensitivityTier}, or `null` when the value is not a recognised tier
 * (so the badge falls back to a neutral style instead of crashing).
 */
const KNOWN_TIERS: readonly SensitivityTier[] = [
  'ITAR',
  'CUI',
  'PHI',
  'comp',
  'review',
  'none',
];

export function asSensitivityTier(value: string): SensitivityTier | null {
  // Case-insensitive: the API serialises every tier uppercase (NONE, COMP,
  // REVIEW) while three of the tokens here are lowercase. Matching exactly
  // meant those three fell through as "unknown" and lost their badge colour.
  const match = KNOWN_TIERS.find(
    (tier) => tier.toLowerCase() === value.toLowerCase(),
  );
  return match ?? null;
}
